/**
 * CalendarDay
 *
 * Single day cell for the monthly calendar grid.
 * Shared between CalendarView (homepage) and CareScreen.
 *
 * Renders:
 * - Background: green tint for today, pink for period-selected, white/5 for normal
 * - Top-right: medication/appointment dot markers
 * - Center: period droplet or fertile sprout icon
 * - Bottom: day number
 */

import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { Droplet, Sprout } from "lucide-react-native";
import { CALENDAR_COLORS, type DaySummary } from "../calendar.types";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export interface CalendarDayProps {
  /** Day number (1-31) or null for empty cell */
  day: number | null;
  /** Day summary with medication/appointment/period/fertile flags */
  summary?: DaySummary;
  /** Whether this day is today */
  isToday: boolean;
  /** Whether this day is selected for period logging (CareScreen) */
  isSelectedForPeriod?: boolean;
  /** Called when the day cell is pressed */
  onPress?: (day: number) => void;
}

export function CalendarDay({
  day,
  summary,
  isToday: isTodayCell,
  isSelectedForPeriod = false,
  onPress,
}: CalendarDayProps) {
  const capturePress = usePressCapture("CalendarDay");
  const isPeriod = summary?.isPeriodDay ?? false;
  const isFertile = summary?.isFertileDay ?? false;
  const isOvulation = summary?.isOvulationDay ?? false;
  const isPredictedPeriod = summary?.isPredictedPeriodDay ?? false;
  const isPredictedFertile = summary?.isPredictedFertileDay ?? false;
  const isPredictedOvulation = summary?.isPredictedOvulationDay ?? false;
  const hasMarkers = summary?.hasMedication || summary?.hasAppointment;
  const hasContent =
    isPeriod ||
    isFertile ||
    isPredictedPeriod ||
    isPredictedFertile ||
    hasMarkers;

  // Accessible description so logged vs estimated vs predicted days are
  // distinguishable to screen readers, not just visually.
  const stateLabel = ((): string | null => {
    if (isPeriod) {
      return "period day";
    }
    if (isFertile) {
      return isOvulation ? "estimated ovulation day" : "fertile window";
    }
    if (isPredictedPeriod) {
      return "predicted period";
    }
    if (isPredictedFertile) {
      return isPredictedOvulation
        ? "predicted ovulation day"
        : "predicted fertile window";
    }
    return null;
  })();
  const a11yParts: string[] = [];
  if (stateLabel) a11yParts.push(stateLabel);
  if (summary?.hasMedication) a11yParts.push("medication");
  if (summary?.hasAppointment) a11yParts.push("appointment");
  const accessibilityLabel =
    day === null
      ? undefined
      : a11yParts.length > 0
        ? `Day ${day}, ${a11yParts.join(", ")}`
        : `Day ${day}`;

  return (
    <Pressable
      onPress={() => {
        if (day === null) return;
        capturePress(pressLabel(accessibilityLabel), "calendar_day");
        onPress?.(day);
      }}
      disabled={day === null}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={{
        flex: 1,
        aspectRatio: 4 / 5,
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: 6,
        paddingBottom: 6,
        borderRadius: 16,
        position: "relative",
        overflow: "hidden",
        backgroundColor: isSelectedForPeriod
          ? "rgba(244, 114, 182, 0.25)"
          : isTodayCell
            ? "rgba(74, 222, 128, 0.2)"
            : day !== null
              ? "rgba(255, 255, 255, 0.05)"
              : "transparent",
        borderWidth: 1,
        borderColor: isSelectedForPeriod
          ? "rgba(244, 114, 182, 0.6)"
          : isTodayCell
            ? "rgba(74, 222, 128, 0.5)"
            : "transparent",
        ...(isSelectedForPeriod && {
          shadowColor: "rgba(244, 114, 182, 0.3)",
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 1,
          shadowRadius: 15,
        }),
        ...(isTodayCell &&
          !isSelectedForPeriod && {
            shadowColor: "rgba(74, 222, 128, 0.1)",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 1,
            shadowRadius: 20,
          }),
      }}
    >
      {/* Day markers - top right corner */}
      {day !== null && hasMarkers && (
        <View
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            flexDirection: "row",
            gap: 2,
          }}
        >
          {summary?.hasMedication && (
            <View
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: CALENDAR_COLORS.medication,
                shadowColor: CALENDAR_COLORS.medication,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.6,
                shadowRadius: 4,
              }}
            />
          )}
          {summary?.hasAppointment && (
            <View
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: CALENDAR_COLORS.appointment,
                shadowColor: CALENDAR_COLORS.appointment,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.6,
                shadowRadius: 4,
              }}
            />
          )}
        </View>
      )}

      {/*
        Period/Fertile icon area. Two visual axes, both built from the
        existing icon + color vocabulary (no new tokens):
        - logged/current = filled + glow; predicted/future = outline + faded
        - ovulation (peak) = filled Sprout; other fertile days = outline Sprout
        Priority: logged period > logged fertile > predicted period > predicted fertile.
      */}
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        {day !== null && isPeriod && (
          <Droplet
            size={20}
            color={CALENDAR_COLORS.period}
            fill={CALENDAR_COLORS.period}
            style={{
              filter: `drop-shadow(0 0 8px ${CALENDAR_COLORS.period}60)`,
            }}
          />
        )}
        {day !== null && isFertile && !isPeriod && (
          <Sprout
            size={20}
            color={CALENDAR_COLORS.fertile}
            fill={isOvulation ? CALENDAR_COLORS.fertile : "transparent"}
            style={{
              filter: `drop-shadow(0 0 8px ${CALENDAR_COLORS.fertile}60)`,
            }}
          />
        )}
        {day !== null && isPredictedPeriod && !isPeriod && !isFertile && (
          <Droplet
            size={20}
            color={CALENDAR_COLORS.period}
            fill="transparent"
            style={{ opacity: 0.5 }}
          />
        )}
        {day !== null &&
          isPredictedFertile &&
          !isPeriod &&
          !isFertile &&
          !isPredictedPeriod && (
            <Sprout
              size={20}
              color={CALENDAR_COLORS.fertile}
              fill={
                isPredictedOvulation ? CALENDAR_COLORS.fertile : "transparent"
              }
              style={{ opacity: 0.5 }}
            />
          )}
      </View>

      {/* Day number */}
      {day !== null && (
        <Text
          style={{
            fontSize: 12,
            fontWeight: isTodayCell ? "600" : hasContent ? "500" : "400",
            color: isTodayCell ? "#ffffff" : hasContent ? "#e2e8f0" : "#94a3b8",
          }}
        >
          {day}
        </Text>
      )}
    </Pressable>
  );
}
